import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award, Star, Medal, Trophy, Flame, Crown, Target, Calculator,
  BookOpen, Lock, AlertCircle, Sparkles
} from 'lucide-react';
import { client } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface Badge {
  id: number;
  name: string;
  nameCn: string;
  description: string;
  icon: string;
  category: string;
  conditionType: string;
  conditionValue: number;
  points: number;
}

interface UserBadge {
  id: number;
  badgeId: number;
  earnedAt: number;
  badge: Badge;
}

const iconMap: Record<string, React.ComponentType<any>> = {
  star: Star,
  medal: Medal,
  trophy: Trophy,
  flame: Flame,
  crown: Crown,
  target: Target,
  bullseye: Target,
  calculator: Calculator,
  book: BookOpen,
};

const categoryColors: Record<string, { bg: string; text: string; gradient: string }> = {
  milestone: { bg: 'bg-blue-50', text: 'text-blue-600', gradient: 'from-blue-500 to-indigo-500' },
  streak: { bg: 'bg-orange-50', text: 'text-orange-600', gradient: 'from-orange-500 to-red-500' },
  accuracy: { bg: 'bg-emerald-50', text: 'text-emerald-600', gradient: 'from-emerald-500 to-teal-500' },
  subject: { bg: 'bg-purple-50', text: 'text-purple-600', gradient: 'from-purple-500 to-pink-500' },
  achievement: { bg: 'bg-amber-50', text: 'text-amber-600', gradient: 'from-amber-500 to-yellow-500' },
};

export function BadgesPage() {
  const { user } = useAuthStore();
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBadges, setNewBadges] = useState<Badge[]>([]);
  const [showNewBadgeModal, setShowNewBadgeModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [user]);

  async function loadData() {
    setLoading(true);
    try {
      const [allRes, userRes] = await Promise.all([
        client.api.fetch('/api/public/badges'),
        user ? client.api.fetch('/api/badges') : Promise.resolve({ json: () => ({ data: [] }) }),
      ]);
      
      const allData = await allRes.json();
      const userData = await userRes.json();
      
      setAllBadges(allData.data || []);
      setUserBadges(userData.data || []);
      
      // Check for new badges
      if (user) {
        const checkRes = await client.api.fetch('/api/badges/check', { method: 'POST' });
        const checkData = await checkRes.json();
        if (checkData.data?.newBadges?.length > 0) {
          setNewBadges(checkData.data.newBadges);
          setShowNewBadgeModal(true);
          // Reload user badges
          const refreshRes = await client.api.fetch('/api/badges');
          const refreshData = await refreshRes.json();
          setUserBadges(refreshData.data || []);
        }
      }
    } catch (error) {
      console.error('Failed to load badges:', error);
    } finally {
      setLoading(false);
    }
  }

  const earnedBadgeIds = new Set(userBadges.map(b => b.badgeId));
  const totalPoints = userBadges.reduce((sum, b) => sum + (b.badge.points || 0), 0);

  // Group badges by category
  const badgesByCategory = allBadges.reduce((acc, badge) => {
    const cat = badge.category || 'achievement';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(badge);
    return acc;
  }, {} as Record<string, Badge[]>);

  const categoryNames: Record<string, string> = {
    milestone: '练习里程碑',
    streak: '连续学习',
    accuracy: '准确率成就',
    subject: '学科达人',
    achievement: '其他成就',
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-slate-900 mb-2">徽章成就</h1>
          <p className="text-slate-600">完成学习任务，解锁专属徽章</p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
        >
          <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <span className="text-white/80 text-sm">已获得徽章</span>
            </div>
            <div className="text-3xl font-bold">{userBadges.length}</div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <span className="text-white/80 text-sm">徽章积分</span>
            </div>
            <div className="text-3xl font-bold">{totalPoints}</div>
          </div>
          
          <div className="bg-white rounded-2xl p-5 border border-slate-100 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Lock className="w-5 h-5 text-slate-400" />
              </div>
              <span className="text-slate-500 text-sm">待解锁</span>
            </div>
            <div className="text-3xl font-bold text-slate-900">
              {allBadges.length - userBadges.length}
            </div>
          </div>
        </motion.div>

        {/* Badge Categories */}
        {Object.entries(badgesByCategory).map(([category, badges], catIndex) => {
          const colors = categoryColors[category] || categoryColors.achievement;
          
          return (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + catIndex * 0.1 }}
              className="mb-8"
            >
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full bg-gradient-to-r ${colors.gradient}`} />
                {categoryNames[category] || category}
              </h2>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {badges.map((badge, index) => {
                  const isEarned = earnedBadgeIds.has(badge.id);
                  const IconComponent = iconMap[badge.icon] || Star;
                  const earnedBadge = userBadges.find(b => b.badgeId === badge.id);
                  
                  return (
                    <motion.div
                      key={badge.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      className={`relative rounded-2xl p-5 border transition-all ${
                        isEarned
                          ? `${colors.bg} ${colors.text} border-transparent shadow-lg`
                          : 'bg-white border-slate-100 opacity-60'
                      }`}
                    >
                      {isEarned && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                      
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                        isEarned ? 'bg-white/60' : 'bg-slate-100'
                      }`}>
                        <IconComponent className={`w-6 h-6 ${isEarned ? colors.text : 'text-slate-400'}`} />
                      </div>
                      
                      <h3 className={`font-semibold mb-1 ${isEarned ? colors.text : 'text-slate-700'}`}>
                        {badge.nameCn}
                      </h3>
                      <p className={`text-sm ${isEarned ? `${colors.text} opacity-80` : 'text-slate-500'}`}>
                        {badge.description}
                      </p>
                      
                      <div className={`mt-3 text-xs font-medium ${isEarned ? colors.text : 'text-slate-400'}`}>
                        +{badge.points} 积分
                      </div>
                      
                      {isEarned && earnedBadge && (
                        <div className={`mt-2 text-xs ${colors.text} opacity-70`}>
                          获得于 {new Date(earnedBadge.earnedAt * 1000).toLocaleDateString('zh-CN')}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}

        {/* Empty State */}
        {allBadges.length === 0 && (
          <div className="text-center py-12">
            <Award className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">暂无徽章</h3>
            <p className="text-slate-500">开始学习后将解锁精彩徽章</p>
          </div>
        )}

        {/* New Badge Modal */}
        {showNewBadgeModal && newBadges.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Award className="w-10 h-10 text-white" />
              </div>
              
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                🎉 恭喜获得新徽章！
              </h2>
              
              <div className="space-y-3 my-6">
                {newBadges.map(badge => (
                  <div key={badge.id} className="bg-amber-50 rounded-xl p-4">
                    <div className="font-semibold text-amber-700">{badge.nameCn}</div>
                    <div className="text-sm text-amber-600">{badge.description}</div>
                    <div className="text-xs text-amber-500 mt-1">+{badge.points} 积分</div>
                  </div>
                ))}
              </div>
              
              <button
                onClick={() => setShowNewBadgeModal(false)}
                className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                太棒了！
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
